import { ISchemaObject } from './schema-object';
import { AnnotationMetaData } from './annotations';

/**
 * The path to an item in the session. Each element must be unique within its
 * parent (similar to a file path). Case-sensitive.
 */
export type ItemPath = string[];

export type ItemPathKey = string;

export type OperationType = string;

/**
 * Represents a single item in the session (such as a tile or an annotation)
 */
export interface Item {
    /**
     * The identifier of this item (unique within its parent). Case-sensitive.
     */
    id: string;

    /**
     * Optional identity of the source this item (used to group related items)
     */
    source?: string;

    /**
     * The revision that identifies the current state of this item (must be
     * unique for this item). Optional for operations that do not change the
     * state of existing items (such as focus and visible).
     */
    revId?: string;

    /**
     * The identifier of the scope item from which this item was originally
     * generated. This attribute groups item related to an execution of the
     * JoinWorkflow that the scope item is related. Optional for items that
     * do not belong to a JoinWorkflow scope.
     */
    contextId?: string;

    /**
     * The contents of the item. Optional for operations that do not change
     * the state of existing items (such as focus and visible).
     */
    body?: ISchemaObject;
}

export interface IItemReference {
    /**
     * Fully qualified path to the referenced tile.
     * Example: ['session', 'docId', 'referencedTileId']
     */
    readonly referencedPath: ItemPath;
}

export enum ItemOperation {
    None,
    Added,
    Updated,
    Deleted
}

/**
 * Item deltas are diffs between a current revision of a tile and the expected new revision.
 * Deltas contain all the information required to transform the current tile revision to the
 * new one and are self contained.
 */
export interface IItemDelta extends ISchemaObject { }

/**
 * Types of changes for tiles.
 * What each change means depends on specific scenario.
 */
 export enum ItemChanges {
    /**
     * TextTile: Content of tile has changed.
     */
    ContentChanged,
    /**
     * TextTile: Content was previously empty (null, undefined, only white spaces).
     */
    ContentWasEmpty,
    /**
     * FormattedTextTile: Formatting has changed.
     */
    FormattingChanged,
}

/**
 * Delta containing information about tile changes since last workflow execution.
 */
export interface IItemChangesDelta extends IItemDelta {
    changes: ItemChanges[];
}

export interface ItemWithPath extends Item {
    parentPath: ItemPath;
    delta?: IItemDelta;
    op?: ItemOperation;
}

/**
 * Base class for all operations (atomic modifications to the session state)
 */
export interface IOperation extends ISchemaObject {
    /**
     * The path to the parent of the items involved in the operation. Empty if this operation
     * applies to the root item.
     */
    parentPath: ItemPath;

    /**
     * The optional expected revision of the parent item in order for this operation to be
     * consistent with the state of the parent. Typically used by operations modifying
     * annotations.
     */
    parentRevId?: string;

    /**
     * The set of items involved in the operation. If multiple items are provided, they must
     * be contiguous siblings. Each item is modified in sequence (so order matters) and the
     * overall operation is considered atomic; a session that has modified only some of the
     * items is not in a valid state.
     */
    items: Item[];
}

/**
 * Base class for Add/Move operations
 */
export interface IOperationWithSiblingContext extends IOperation {
    /**
     * Previous sibling (reserved string '#head#' indicates this is the first sibling), identified
     * by its Item.id field. If not supplied, ordering WRT items not included in this operation is
     * determined by operation sequence order (items are added to the end by default).
     */
    prevId?: string;

    /**
     * Next sibling (reserved string '#tail#' indicates this is the last sibling), identified
     * by its Item.id field. If not supplied, ordering WRT items not included in this operation is
     * determined by operation sequence order (items are added to the end by default).
     */
    nextId?: string;
}

/**
 * Adds one or more items to the session state. See base class for sibling context.
 */
export interface IAddOperation extends IOperationWithSiblingContext { }

/**
 * Moves one or more items to a different parent. See base class for sibling context.
 */
export interface IMoveOperation extends IOperationWithSiblingContext {
    /**
     * The parent path from which the items are moving
     */
    prevParentPath: ItemPath;
}

export interface IUpdateAnnotationMetaDataOperation extends IOperation {
    /**
     * @cppname Metadata
     */
    M_: AnnotationMetaData;
}

/**
 * Updates the entire content of one or more items.
 */
export interface IUpdateOperation extends IOperation { }

/**
 * Deletes one or more items, and their children, from the session state.
 */
export interface IDeleteOperation extends IOperation { }

/**
 * Purges an item (and its subtree), keeping everything else.
 * This is an efficient way to remove a large portion of the model.
 * Use only when the portion removed is much larger than what will
 * be left. For example deleting the whole document.
 *
 * Side effects of this operations is canceling all notifications
 * to workflows and not triggering any notifications for the removed
 * portion. Use at your own risk.
 */
export interface IPurgeOperation extends IOperation { }

/**
 * Indicates that one or more items have gained or lost user focus.
 */
export interface IFocusOperation extends IOperation {
    /**
     * The new focus state
     */
    isFocused: boolean;
}

/**
 * Indicates that one or more items have gained or lost user visibility.
 */
export interface IVisibilityOperation extends IOperation {
    /**
     * The new visibility state
     */
    isVisible: boolean;
}

/**
 * A more efficient update operation that uses deltas instead of full tiles for
 * hosts that support producing tile deltas.
 */
export interface IDeltaUpdateOperation extends IOperation { }


/**
 * An operation used by the client to send a MicroSyncMessage to the server.
 */
export interface IMicroSyncOperation extends IOperation {
    /**
     * Sequence number (message is applied on receipt if omitted).
     * Not yet implemented by server
     */
    readonly seq?: number;

    /**
     * One of 'Add', 'Update', 'Delete' (defaults to 'Add' if omitted).
     * Not yet implemented by server
     */
    readonly opType?: string;
}

/**
 * Contains any signals that occurred during the session.
 * @typeprefix AugLoop_Signals
 */
export interface ISignalOperation extends IOperation {
}
